/**
 * Video Fan Carousel — custom centre-anchored deck (no Swiper).
 */

(function () {
  'use strict';

  const GAP = 16;
  const SPEED = 700;
  const DRAG_THRESHOLD = 40;
  const PLAY_DRAG_THRESHOLD = 6;

  function prefersReducedMotion() {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }

  function lerp(start, end, progress) {
    return start + (end - start) * progress;
  }

  function easeOutCubic(progress) {
    return 1 - Math.pow(1 - progress, 3);
  }

  function getMaxVisibleOffset() {
    if (window.matchMedia('(min-width: 1050px)').matches) return 3;
    if (window.matchMedia('(min-width: 768px)').matches) return 2;
    return 1;
  }

  function resetVideoCard(card) {
    if (!card) return;

    const poster = card.querySelector('[data-vfc-poster]');
    const playBtn = card.querySelector('[data-vfc-play-btn]');
    const video = card.querySelector('.video-fan-carousel__video');

    if (video && !video.paused) {
      video.pause();
    }

    if (video) {
      video.muted = true;
    }

    if (poster) {
      poster.classList.remove('video-fan-carousel__poster--hidden');
    }

    if (playBtn) {
      playBtn.setAttribute('aria-pressed', 'false');
      playBtn.setAttribute('aria-label', playBtn.dataset.vfcPlayLabel || 'Play video');
      playBtn.classList.remove('video-fan-carousel__play-btn--hidden');
    }
  }

  function pauseAllVideos(section, exceptVideo) {
    section.querySelectorAll('.video-fan-carousel__video').forEach((video) => {
      if (video === exceptVideo) return;
      resetVideoCard(video.closest('[data-vfc-card]'));
    });
  }

  function bindVideoPlay(section) {
    section.querySelectorAll('[data-vfc-card]').forEach((card) => {
      const video = card.querySelector('.video-fan-carousel__video');
      const playBtn = card.querySelector('[data-vfc-play-btn]');
      if (!video || !playBtn || card.dataset.vfcVideoBound === 'true') return;

      card.dataset.vfcVideoBound = 'true';

      const poster = card.querySelector('[data-vfc-poster]');
      let startX = 0;
      let startY = 0;

      function showPoster() {
        if (poster) poster.classList.remove('video-fan-carousel__poster--hidden');
        playBtn.setAttribute('aria-pressed', 'false');
        playBtn.setAttribute('aria-label', playBtn.dataset.vfcPlayLabel || 'Play video');
        playBtn.classList.remove('video-fan-carousel__play-btn--hidden');
      }

      function hidePoster() {
        if (poster) poster.classList.add('video-fan-carousel__poster--hidden');
        playBtn.setAttribute('aria-pressed', 'true');
        playBtn.setAttribute('aria-label', playBtn.dataset.vfcPauseLabel || 'Pause video');
        playBtn.classList.add('video-fan-carousel__play-btn--hidden');
      }

      function togglePlayback() {
        if (video.paused) {
          pauseAllVideos(section, video);
          video.muted = false;
          const playPromise = video.play();
          if (playPromise && typeof playPromise.then === 'function') {
            playPromise.then(hidePoster).catch(showPoster);
          } else {
            hidePoster();
          }
        } else {
          video.pause();
          showPoster();
        }
      }

      card.addEventListener('pointerdown', (event) => {
        startX = event.clientX;
        startY = event.clientY;
      });

      card.addEventListener('pointerup', (event) => {
        const dx = Math.abs(event.clientX - startX);
        const dy = Math.abs(event.clientY - startY);
        if (dx > PLAY_DRAG_THRESHOLD || dy > PLAY_DRAG_THRESHOLD) return;
        if (playBtn.contains(event.target)) return;
        togglePlayback();
      });

      playBtn.addEventListener('click', (event) => {
        event.preventDefault();
        togglePlayback();
      });

      video.addEventListener('ended', showPoster);
    });
  }

  class VideoFanDeck {
    constructor(section, deckEl) {
      this.section = section;
      this.deckEl = deckEl;
      this.stage = deckEl.querySelector('[data-vfc-stage]');
      this.slides = [...deckEl.querySelectorAll('[data-vfc-slide]')];
      this.statusEl = deckEl.querySelector('[data-vfc-status]');
      this.statusTemplate = deckEl.dataset.slideStatusTemplate || '';
      this.slideCount = this.slides.length;
      this.loop = deckEl.dataset.loop === 'true' && this.slideCount >= 3;
      this.activeIndex = 0;
      this.transitionMs = prefersReducedMotion() ? 0 : SPEED;
      this.isAnimating = false;
      this.drag = null;
      this.resizeObserver = null;
      this.resizeTimer = null;
      this.animateTimer = null;
      this.animateFrame = null;
      this.designTierDimensions = null;

      this.onResize = this.onResize.bind(this);
      this.onKeyDown = this.onKeyDown.bind(this);
      this.onPointerDown = this.onPointerDown.bind(this);
      this.onPointerMove = this.onPointerMove.bind(this);
      this.onPointerUp = this.onPointerUp.bind(this);
    }

    init() {
      if (!this.stage || this.slideCount === 0) return;

      this.stage.addEventListener('keydown', this.onKeyDown);
      this.stage.addEventListener('pointerdown', this.onPointerDown);
      this.stage.addEventListener('pointermove', this.onPointerMove);
      this.stage.addEventListener('pointerup', this.onPointerUp);
      this.stage.addEventListener('pointercancel', this.onPointerUp);

      if (typeof ResizeObserver !== 'undefined') {
        this.resizeObserver = new ResizeObserver(this.onResize);
        this.resizeObserver.observe(this.stage);
      } else {
        window.addEventListener('resize', this.onResize);
      }

      requestAnimationFrame(() => {
        this.layout();
        requestAnimationFrame(() => {
          this.layout();
        });
      });
    }

    destroy() {
      if (this.resizeTimer) {
        clearTimeout(this.resizeTimer);
      }

      if (this.animateTimer) {
        clearTimeout(this.animateTimer);
      }

      if (this.animateFrame) {
        cancelAnimationFrame(this.animateFrame);
      }

      if (this.stage) {
        this.stage.classList.remove('video-fan-carousel__stage--navigating');
        this.clearInlineSlideStyles();
        this.stage.removeEventListener('keydown', this.onKeyDown);
        this.stage.removeEventListener('pointerdown', this.onPointerDown);
        this.stage.removeEventListener('pointermove', this.onPointerMove);
        this.stage.removeEventListener('pointerup', this.onPointerUp);
        this.stage.removeEventListener('pointercancel', this.onPointerUp);
      }

      if (this.resizeObserver) {
        this.resizeObserver.disconnect();
      } else {
        window.removeEventListener('resize', this.onResize);
      }
    }

    onResize() {
      clearTimeout(this.resizeTimer);
      this.resizeTimer = window.setTimeout(() => {
        this.layout();
      }, 100);
    }

    onKeyDown(event) {
      if (event.key === 'ArrowLeft') {
        event.preventDefault();
        this.prev();
      } else if (event.key === 'ArrowRight') {
        event.preventDefault();
        this.next();
      }
    }

    onPointerDown(event) {
      if (event.button !== 0) return;
      if (event.target.closest('[data-vfc-play-btn]')) return;

      this.drag = {
        startX: event.clientX,
        startY: event.clientY,
        pointerId: event.pointerId,
      };

      this.stage.classList.add('video-fan-carousel__stage--dragging');
      this.stage.setPointerCapture(event.pointerId);
    }

    onPointerMove(event) {
      if (!this.drag || event.pointerId !== this.drag.pointerId) return;
      this.drag.currentX = event.clientX;
      this.drag.currentY = event.clientY;
    }

    onPointerUp(event) {
      if (!this.drag || event.pointerId !== this.drag.pointerId) return;

      const dx = (this.drag.currentX ?? event.clientX) - this.drag.startX;
      const dy = (this.drag.currentY ?? event.clientY) - this.drag.startY;

      this.stage.classList.remove('video-fan-carousel__stage--dragging');

      if (this.stage.hasPointerCapture(event.pointerId)) {
        this.stage.releasePointerCapture(event.pointerId);
      }

      this.drag = null;

      if (Math.abs(dx) < DRAG_THRESHOLD || Math.abs(dx) < Math.abs(dy)) return;

      if (dx < 0) {
        this.next();
      } else {
        this.prev();
      }
    }

    wrapOffset(index) {
      let offset = index - this.activeIndex;

      if (!this.loop) return offset;

      const count = this.slideCount;
      offset = ((offset % count) + count) % count;
      if (offset > count / 2) offset -= count;

      return offset;
    }

    getMaxOffset() {
      const designMax = getMaxVisibleOffset();
      const dataMax = Math.floor((this.slideCount - 1) / 2);
      return Math.min(designMax, dataMax);
    }

    normalizeIndex(index) {
      if (this.loop) {
        return ((index % this.slideCount) + this.slideCount) % this.slideCount;
      }

      return Math.max(0, Math.min(this.slideCount - 1, index));
    }

    computeX(offset, tierWidths) {
      if (offset === 0) return 0;

      const sign = Math.sign(offset);
      let x = 0;

      for (let step = 1; step <= Math.abs(offset); step++) {
        const wInner = tierWidths.get(step - 1) ?? tierWidths.get(0) ?? 0;
        const wOuter = tierWidths.get(step) ?? tierWidths.get(3) ?? wInner;
        x += sign * (wInner / 2 + GAP + wOuter / 2);
      }

      return x;
    }

    collectTierDimensions(maxTier) {
      const widths = new Map();
      const heights = new Map();

      for (let tier = 0; tier <= maxTier; tier++) {
        const slide = this.slides.find((item) => {
          const sizeOffset = parseInt(item.dataset.sizeOffset ?? item.dataset.offset, 10);
          return !Number.isNaN(sizeOffset) && Math.abs(sizeOffset) === tier;
        });

        if (slide && slide.offsetWidth > 0) {
          widths.set(tier, slide.offsetWidth);
          heights.set(tier, slide.offsetHeight);
        }
      }

      for (let tier = 0; tier <= maxTier; tier++) {
        if (widths.has(tier)) continue;

        const fallbackTier = widths.has(tier - 1) ? tier - 1 : tier + 1;
        const fallbackWidth = widths.get(fallbackTier) ?? widths.get(0);
        const fallbackHeight = heights.get(fallbackTier) ?? heights.get(0);

        if (fallbackWidth) widths.set(tier, fallbackWidth);
        if (fallbackHeight) heights.set(tier, fallbackHeight);
      }

      return { widths, heights };
    }

    getTierDimensions(offset, maxVisible, dimensions) {
      const tier = Math.min(Math.abs(offset), maxVisible);

      return {
        width: dimensions.widths.get(tier) ?? dimensions.widths.get(0) ?? 0,
        height: dimensions.heights.get(tier) ?? dimensions.heights.get(0) ?? 0,
      };
    }

    isPeekOffset(offset, maxVisible) {
      return Math.abs(offset) >= maxVisible;
    }

    isWrapTransition(from, to) {
      return Math.abs(to.offset - from.offset) > 1;
    }

    hideSlide(slide) {
      slide.style.visibility = 'hidden';
      slide.style.opacity = '0';
      slide.setAttribute('aria-hidden', 'true');
      slide.setAttribute('inert', '');
    }

    setSlideX(slide, x) {
      slide.style.setProperty('--vfc-x', `${x}px`);
      slide.style.transform = `translate3d(calc(-50% + ${x}px), -50%, 0)`;
    }

    getSlideX(slide) {
      const value = slide.style.getPropertyValue('--vfc-x').trim();
      return value ? parseFloat(value) : 0;
    }

    setSlideVisual(slide, state, maxVisible) {
      const offset = state.offset;
      const inView = Math.abs(offset) <= maxVisible;

      slide.style.width = `${state.width}px`;
      slide.style.height = `${state.height}px`;
      slide.style.transform = `translate3d(calc(-50% + ${state.x}px), -50%, 0)`;
      slide.style.setProperty('--vfc-x', `${state.x}px`);
      slide.style.opacity = '1';
      slide.style.zIndex = String(Math.max(1, Math.round(state.width)));
      slide.style.visibility = inView ? 'visible' : 'hidden';
      slide.classList.toggle('video-fan-carousel__slide--peek', this.isPeekOffset(offset, maxVisible));
      slide.setAttribute('aria-hidden', inView ? 'false' : 'true');

      if (inView) {
        slide.removeAttribute('inert');
      } else {
        slide.setAttribute('inert', '');
      }
    }

    clearInlineSlideStyles() {
      this.slides.forEach((slide) => {
        slide.style.width = '';
        slide.style.height = '';
        slide.style.zIndex = '';
        slide.style.visibility = '';
        slide.style.opacity = '';
      });
    }

    captureLayoutState() {
      const states = new Map();

      this.slides.forEach((slide) => {
        states.set(slide, {
          offset: parseInt(slide.dataset.offset, 10),
          x: this.getSlideX(slide),
          width: slide.offsetWidth,
          height: slide.offsetHeight,
        });
      });

      return states;
    }

    buildLayoutState(activeIndex) {
      const savedIndex = this.activeIndex;
      const maxVisible = this.getMaxOffset();
      const dimensions = this.designTierDimensions || this.collectTierDimensions(maxVisible);
      const states = new Map();

      this.activeIndex = activeIndex;

      this.slides.forEach((slide, index) => {
        const offset = this.wrapOffset(index);
        const { width, height } = this.getTierDimensions(offset, maxVisible, dimensions);

        states.set(slide, {
          offset,
          x: this.computeX(offset, dimensions.widths),
          width,
          height,
        });
      });

      this.activeIndex = savedIndex;
      return states;
    }

    layout() {
      const maxVisible = this.getMaxOffset();
      const dimensions = this.collectTierDimensions(maxVisible);

      this.designTierDimensions = {
        widths: new Map(dimensions.widths),
        heights: new Map(dimensions.heights),
      };

      this.slides.forEach((slide, index) => {
        slide.dataset.offset = String(this.wrapOffset(index));
        slide.dataset.sizeOffset = slide.dataset.offset;
      });

      void this.stage.offsetHeight;

      this.clearInlineSlideStyles();

      this.slides.forEach((slide) => {
        const offset = parseInt(slide.dataset.offset, 10);
        const x = this.computeX(offset, this.designTierDimensions.widths);

        this.setSlideX(slide, x);
        slide.classList.toggle('video-fan-carousel__slide--peek', this.isPeekOffset(offset, maxVisible));

        const inView = Math.abs(offset) <= maxVisible;
        slide.setAttribute('aria-hidden', inView ? 'false' : 'true');

        if (inView) {
          slide.removeAttribute('inert');
        } else {
          slide.setAttribute('inert', '');
          resetVideoCard(slide.querySelector('[data-vfc-card]'));
        }
      });

      this.updateStatus();

      if (!this.stage.dataset.vfcReady) {
        this.stage.dataset.vfcReady = 'true';
      }
    }

    updateStatus() {
      if (!this.statusEl) return;

      const activeIndex = this.activeIndex + 1;
      this.statusEl.textContent = this.statusTemplate
        ? this.statusTemplate
            .replace('__INDEX__', String(activeIndex))
            .replace('__COUNT__', String(this.slideCount))
        : `Slide ${activeIndex} of ${this.slideCount}`;
    }

    goTo(index) {
      if (this.isAnimating || this.slideCount === 0) return;

      const target = this.normalizeIndex(index);
      if (target === this.activeIndex) return;

      if (this.transitionMs <= 0) {
        this.activeIndex = target;
        this.layout();
        return;
      }

      const maxVisible = this.getMaxOffset();
      const fromState = this.captureLayoutState();
      const toState = this.buildLayoutState(target);

      this.isAnimating = true;
      this.stage.classList.add('video-fan-carousel__stage--navigating');

      const start = performance.now();

      const frame = (now) => {
        const raw = Math.min(1, (now - start) / this.transitionMs);
        const eased = easeOutCubic(raw);

        this.slides.forEach((slide) => {
          const from = fromState.get(slide);
          const to = toState.get(slide);
          if (!from || !to) return;

          // Loop-wrap cards jump across the deck — hide them instead of sweeping through centre
          if (this.isWrapTransition(from, to)) {
            this.hideSlide(slide);
            return;
          }

          const wasVisible = Math.abs(from.offset) <= maxVisible;
          const willBeVisible = Math.abs(to.offset) <= maxVisible;
          const revealProgress = willBeVisible && !wasVisible ? Math.min(1, raw * 4) : eased;

          this.setSlideVisual(
            slide,
            {
              offset: lerp(from.offset, to.offset, raw),
              x: lerp(from.x, to.x, raw),
              width: lerp(from.width, to.width, revealProgress),
              height: lerp(from.height, to.height, revealProgress),
            },
            maxVisible
          );
        });

        if (raw < 1) {
          this.animateFrame = requestAnimationFrame(frame);
          return;
        }

        this.activeIndex = target;
        this.stage.classList.remove('video-fan-carousel__stage--navigating');
        this.clearInlineSlideStyles();
        this.layout();
        this.isAnimating = false;
        this.animateFrame = null;
      };

      this.animateFrame = requestAnimationFrame(frame);
    }

    prev() {
      this.goTo(this.activeIndex - 1);
    }

    next() {
      this.goTo(this.activeIndex + 1);
    }
  }

  const deckInstances = new WeakMap();

  function initSection(section) {
    if (!section || section.dataset.vfcInited === 'true') return;

    const deckEl = section.querySelector('[data-vfc-deck]');
    if (!deckEl) return;

    const prevBtn = section.querySelector('[data-vfc-prev]');
    const nextBtn = section.querySelector('[data-vfc-next]');

    const deck = new VideoFanDeck(section, deckEl);
    deck.init();
    deckInstances.set(section, deck);

    if (prevBtn) {
      prevBtn.addEventListener('click', () => deck.prev());
    }

    if (nextBtn) {
      nextBtn.addEventListener('click', () => deck.next());
    }

    bindVideoPlay(section);
    section.dataset.vfcInited = 'true';
  }

  function destroySection(section) {
    const deck = deckInstances.get(section);
    if (deck) {
      deck.destroy();
      deckInstances.delete(section);
    }

    delete section.dataset.vfcInited;

    section.querySelectorAll('[data-vfc-card]').forEach((card) => {
      delete card.dataset.vfcVideoBound;
    });
  }

  function initAll(container) {
    const root = container || document;
    root.querySelectorAll('[data-video-fan-carousel]').forEach(initSection);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll);
  } else {
    initAll();
  }

  document.addEventListener('shopify:section:load', (event) => {
    initAll(event.target);
  });

  document.addEventListener('shopify:section:unload', (event) => {
    const section = event.target.querySelector('[data-video-fan-carousel]');
    if (section) destroySection(section);
  });
})();
